import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';
import { observer } from 'mobx-react';

import { AnimationKeys } from '../../../../stores/animation/settings/config';
import { useStores } from '../../../../stores/useStores';
import { devError } from '../../../../utils/log';
import { CoinBackSteps } from '../../../coinbackTutorial/constants';
import { useCoinBackTutorial } from '../../../coinbackTutorial/useCoinBackTutorial';
import type { CoinBackWidgetDataType, WidgetSizeType } from '../../types';
import { GeneralSmallWidget, GeneralWideWidget } from '../GeneralWidget';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

import styles from './CoinBackWidget.module.scss';
import { CoinBackWidgetTimer } from './CoinBackWidgetTimer/CoinBackWidgetTimer';

interface CoinBackWidgetProps {
  sizeType: WidgetSizeType;
  data: CoinBackWidgetDataType;
}

const COINBACK_WIDGET_TUTORIAL_OFFSET: [number, number] = [140, 15];

function CoinBackWidgetComponent(props: CoinBackWidgetProps) {
  const { data, sizeType } = props;
  const { event } = data;
  const { coinBackStore, animationStore } = useStores();

  const { currentCoinBackBooster, checkIfBoosterActive, currentCoinBack } = coinBackStore;
  const { showCoinBackTutorial } = useCoinBackTutorial(
    CoinBackSteps.CHECK_FEATURE,
    'bottom',
    COINBACK_WIDGET_TUTORIAL_OFFSET,
  );

  const isBoosterActive = checkIfBoosterActive(currentCoinBackBooster);
  const widgetImageUrl = useMemo(() => {
    if (isBoosterActive) {
      return currentCoinBackBooster.widgetImageUrl || event.defaultBoosterWidgetImageUrl;
    }

    return event.widgetImageUrl;
  }, [
    currentCoinBackBooster?.widgetImageUrl,
    event.defaultBoosterWidgetImageUrl,
    event.widgetImageUrl,
    isBoosterActive,
  ]);

  const handleTimerEnd = useCallback(async () => {
    try {
      coinBackStore.setRunningAnimation(null);
      await animationStore.loadImages(AnimationKeys.SAFE_OPEN);
      await coinBackStore.fetch();
      coinBackStore.closeDialog();

      if (currentCoinBackBooster && dayjs(currentCoinBackBooster.endDate).isBefore(dayjs())) {
        coinBackStore.setCurrentCoinBackBooster(null);
      } else {
        coinBackStore.startDialogs('CoinBack');
      }
    } catch (error) {
      devError('Error CoinBack handling end', error);
    }
  }, [animationStore, coinBackStore, currentCoinBackBooster]);

  const handleClick = () => {
    if (showCoinBackTutorial) {
      return;
    }
    coinBackStore.openDialog('widget_click');
    coinBackStore.sendAnalytics(
      {
        feature_sub_type: 'coinback',
        event_action: 'click',
      },
      {
        triggered_by: 'click',
        click_location: 'lobby',
      },
    );
  };

  const onTimerEnd = useCallback(() => {
    const randomDelayMS = (Math.floor(Math.random() * 10) + 1) * 1000;
    setTimeout(() => {
      void handleTimerEnd();
    }, randomDelayMS);
  }, [handleTimerEnd]);

  const timer = currentCoinBack ? (
    <CoinBackWidgetTimer endDate={event.endDate} extended={sizeType === 'wide'} onEnd={onTimerEnd} />
  ) : null;
  const smallWidgetExtended = !!timer;

  return (
    <WidgetWrapper
      className={styles.container}
      sizeType={sizeType}
      smallWidgetExtended={smallWidgetExtended}
      onClick={handleClick}
    >
      {sizeType === 'small' ? (
        <GeneralSmallWidget timer={timer} widgetImageUrl={widgetImageUrl} />
      ) : (
        <GeneralWideWidget timer={timer} widgetImageUrl={widgetImageUrl} />
      )}
    </WidgetWrapper>
  );
}

export const CoinBackWidget = observer(CoinBackWidgetComponent);
